import React from "react";
import { useNavigate } from "react-router-dom";
import api from "@services/api";
import ZonesEditorMap from "./ZonesEditorMap";
import { clipAgainstOthers, normalizeZones } from "./zoneGeometry";
import "./map-zones.css";

const todayISO = () => new Date().toISOString().slice(0, 10);

// Mounted only when "Show orders" is on. Fetches server-decoded order points
// (reliable ST_X/ST_Y + Jordan-bounds normalization) and lifts them up.
function OrdersLayer({ day, onPoints, onCount }) {
  React.useEffect(() => {
    let alive = true;
    onCount?.({ total: 0, mapped: 0, loading: true });
    api.get("/map-zones/order-points", { params: { date: day || undefined } })
      .then((r) => {
        if (!alive) return;
        const pts = r.data?.data || [];
        onPoints(pts);
        onCount?.({ total: pts.length, mapped: pts.length, loading: false });
      })
      .catch(() => {
        if (!alive) return;
        onPoints([]);
        onCount?.({ total: 0, mapped: 0, loading: false });
      });
    return () => { alive = false; };
  }, [day, onPoints, onCount]);
  return null;
}

// Convert the zones object <-> an editable array so add/remove/rename is easy.
function toList(zonesObj) {
  return Object.entries(zonesObj || {}).map(([name, z]) => ({
    name,
    name_ar: z.name_ar || "",
    zone_code: z.zone_code ?? "",
    color: z.color || "#4A90E2",
    coordinates: (z.coordinates || []).map((c) => ({
      lat: c.lat ?? "",
      lng: c.lng ?? "",
    })),
  }));
}

function toObject(list) {
  const out = {};
  for (const z of list) {
    const key = (z.name || "").trim();
    if (!key) continue;
    out[key] = {
      name_ar: z.name_ar || "",
      zone_code:
        z.zone_code === "" || z.zone_code === null || z.zone_code === undefined
          ? null
          : Number(z.zone_code),
      color: z.color,
      coordinates: z.coordinates
        .filter((c) => c.lat !== "" && c.lng !== "")
        .map((c) => ({ lat: Number(c.lat), lng: Number(c.lng) })),
    };
  }
  return out;
}

export default function MapZonesSettingsPage() {
  const navigate = useNavigate();
  const [zones, setZones] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const [showJson, setShowJson] = React.useState(false);
  const [jsonText, setJsonText] = React.useState("");
  const [jsonMsg, setJsonMsg] = React.useState(null);
  const [selected, setSelected] = React.useState(null);
  const [versions, setVersions] = React.useState([]);
  const [label, setLabel] = React.useState("");
  const [showVersions, setShowVersions] = React.useState(false);
  const [showOrders, setShowOrders] = React.useState(false);
  const [ordersDay, setOrdersDay] = React.useState(todayISO());
  const [orderPoints, setOrderPoints] = React.useState([]);
  const [ordersInfo, setOrdersInfo] = React.useState({ total: 0, mapped: 0, loading: false });

  const loadVersions = React.useCallback(() => {
    api.get("/map-zones/versions").then((r) => setVersions(r.data?.data || [])).catch(() => {});
  }, []);

  const loadVersion = async (v) => {
    try {
      const { data } = await api.get(`/map-zones/versions/${v.id}`);
      setZones(toList(data?.data || {}));
      setSelected(null);
      setMsg({ type: "ok", text: `Loaded version #${v.id}${v.label ? ` — "${v.label}"` : ""} into the editor. Review, then Save to make it current.` });
    } catch {
      setMsg({ type: "err", text: "Could not load that version." });
    }
  };

  // Silently trim area i so it doesn't overlap the others (runs a moment after
  // the user stops editing — no error popups, it just adjusts itself).
  const fixTimer = React.useRef(null);
  const scheduleFix = React.useCallback((i) => {
    clearTimeout(fixTimer.current);
    fixTimer.current = setTimeout(() => {
      setZones((zs) => {
        if (!zs[i]) return zs;
        const others = zs.filter((_, idx) => idx !== i).map((z) => z.coordinates);
        const res = clipAgainstOthers(zs[i].coordinates, others);
        if (!res.changed) return zs;
        setMsg({ type: "ok", text: "Adjusted the area so it doesn't overlap the others." });
        return zs.map((z, idx) => (idx === i ? { ...z, coordinates: res.coords } : z));
      });
    }, 450);
  }, []);

  // Map edited a zone's polygon -> update that zone's coords, then auto-adjust.
  const setZoneCoords = React.useCallback((i, coords) => {
    setZones((zs) => zs.map((z, idx) => (idx === i ? { ...z, coordinates: coords } : z)));
    scheduleFix(i);
  }, [scheduleFix]);

  // A new polygon was drawn on the map -> append it as a new area, select it,
  // and auto-adjust so it doesn't overlap existing areas.
  const addZoneFromDraw = React.useCallback((coords) => {
    setZones((zs) => {
      const newIdx = zs.length;
      setSelected(newIdx);
      scheduleFix(newIdx);
      return [...zs, { name: `Area_${zs.length + 1}`, name_ar: "", zone_code: "", color: "#9013FE", coordinates: coords }];
    });
  }, [scheduleFix]);

  // Pull the current editor state into the JSON box (for copy / tweak).
  const fillJsonFromEditor = React.useCallback(() => {
    setJsonText(JSON.stringify(toObject(zones), null, 2));
    setJsonMsg(null);
  }, [zones]);

  // Parse the pasted JSON and load it into the point-by-point editor.
  const applyJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        throw new Error('Expected an object like { "North_Amman": { ... } }');
      }
      const list = toList(parsed);
      if (!list.length) throw new Error("No zones found in the JSON.");
      const fixed = normalizeZones(list);
      setZones(fixed.zones);
      setJsonMsg({ type: "ok", text: `Loaded ${list.length} area(s)${fixed.changed ? " (adjusted to prevent overlaps)" : ""}. Review, then Save.` });
    } catch (e) {
      setJsonMsg({ type: "err", text: "Invalid JSON: " + e.message });
    }
  };

  const load = React.useCallback(() => {
    setLoading(true);
    api
      .get("/map-zones")
      .then((r) => setZones(toList(r.data?.data || {})))
      .finally(() => setLoading(false));
  }, []);
  React.useEffect(() => {
    load();
    loadVersions();
  }, [load, loadVersions]);

  const setZone = (i, patch) =>
    setZones((zs) => zs.map((z, idx) => (idx === i ? { ...z, ...patch } : z)));
  const setPoint = (zi, pi, patch) =>
    setZones((zs) =>
      zs.map((z, idx) =>
        idx === zi
          ? { ...z, coordinates: z.coordinates.map((c, ci) => (ci === pi ? { ...c, ...patch } : c)) }
          : z
      )
    );
  const addPoint = (zi) =>
    setZones((zs) => zs.map((z, idx) => (idx === zi ? { ...z, coordinates: [...z.coordinates, { lat: "", lng: "" }] } : z)));
  const removePoint = (zi, pi) =>
    setZones((zs) => zs.map((z, idx) => (idx === zi ? { ...z, coordinates: z.coordinates.filter((_, ci) => ci !== pi) } : z)));
  const removeZone = (zi) => setZones((zs) => zs.filter((_, idx) => idx !== zi));
  const addZone = () =>
    setZones((zs) => [
      ...zs,
      { name: "New_Zone", name_ar: "", zone_code: "", color: "#4A90E2", coordinates: [{ lat: "", lng: "" }, { lat: "", lng: "" }, { lat: "", lng: "" }] },
    ]);

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      // Final safety net: make sure no areas overlap before storing.
      const fixed = normalizeZones(zones);
      if (fixed.changed) setZones(fixed.zones);
      const payload = toObject(fixed.zones);
      const { data } = await api.put("/map-zones", { zones: payload, label: label.trim() || undefined });
      setZones(toList(data?.data || payload));
      setLabel("");
      loadVersions();
      setMsg({ type: "ok", text: "Saved. The map will use these zones (saved as a new version)." });
    } catch (e) {
      setMsg({ type: "err", text: e?.response?.data?.message || "Failed to save." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="oc-content mz-page">
      <div className="mz-head">
        <div>
          <h2>Map Zones</h2>
          <p className="mz-muted">
            Edit the coloured areas shown under the orders on the map. Fill the latitude / longitude
            for each corner of every area. At least 3 points per area.
          </p>
        </div>
        <button className="oc-btn" onClick={() => navigate("/sales/map")}>← Back to Map</button>
      </div>

      {msg && <div className={`mz-msg ${msg.type}`}>{msg.text}</div>}

      {/* Saved versions (history of every save). */}
      <div className="mz-json">
        <button className="mz-linkbtn" onClick={() => { const n = !showVersions; setShowVersions(n); if (n) loadVersions(); }}>
          {showVersions ? "▾ " : "▸ "} Saved versions ({versions.length})
        </button>
        {showVersions && (
          <div className="mz-json-body">
            {versions.length === 0 && <div className="mz-muted">No saved versions yet — Save to create the first one.</div>}
            {versions.length > 0 && (
              <table className="mz-ver-table">
                <thead>
                  <tr><th>#</th><th>Label</th><th>Areas</th><th>By</th><th>Saved</th><th></th></tr>
                </thead>
                <tbody>
                  {versions.map((v) => (
                    <tr key={v.id}>
                      <td>{v.id}</td>
                      <td>{v.label || <span className="mz-muted">—</span>}</td>
                      <td>{v.area_count}</td>
                      <td>{v.created_by}</td>
                      <td className="mz-muted">{(v.created_at || "").replace("T", " ").slice(0, 16)}</td>
                      <td><button className="mz-linkbtn" onClick={() => loadVersion(v)}>Load</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Paste-JSON option (in addition to editing point by point below). */}
      <div className="mz-json">
        <button
          className="mz-linkbtn"
          onClick={() => {
            const next = !showJson;
            setShowJson(next);
            if (next) fillJsonFromEditor();
          }}
        >
          {showJson ? "▾ " : "▸ "} Paste / edit as JSON
        </button>
        {showJson && (
          <div className="mz-json-body">
            <p className="mz-muted">
              Paste the whole zones JSON here and click <b>Load into editor</b>, then review and Save.
              Each area: <code>{`{ "name_ar", "zone_code", "color", "coordinates":[{lat,lng}...] }`}</code>.
            </p>
            <textarea
              className="oc-input mz-json-area"
              rows={12}
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              placeholder='{ "North_Amman": { "color": "#4A90E2", "coordinates": [ { "lat": 32.01, "lng": 35.81 } ] } }'
            />
            {jsonMsg && <div className={`mz-msg ${jsonMsg.type}`}>{jsonMsg.text}</div>}
            <div className="mz-actions">
              <button className="oc-btn" onClick={fillJsonFromEditor}>Fill from current</button>
              <div className="mz-spacer" />
              <button className="oc-btn primary" onClick={applyJson}>Load into editor</button>
            </div>
          </div>
        )}
      </div>

      {loading && <div className="mz-muted">Loading…</div>}

      {!loading && (
        <>
          <div className="mz-orders-bar">
            <label className="mz-check">
              <input type="checkbox" checked={showOrders} onChange={(e) => setShowOrders(e.target.checked)} />
              Show orders on map
            </label>
            {showOrders && (
              <>
                <label className="mz-muted">Day <input className="oc-input" type="date" value={ordersDay} onChange={(e) => setOrdersDay(e.target.value)} /></label>
                <span className="mz-muted">
                  {ordersInfo.loading ? "Loading orders…" : `${ordersInfo.mapped} order${ordersInfo.mapped === 1 ? "" : "s"} shown${ordersInfo.total > ordersInfo.mapped ? ` (${ordersInfo.total - ordersInfo.mapped} without coordinates)` : ""}`}
                </span>
              </>
            )}
          </div>
          {showOrders && <OrdersLayer day={ordersDay} onPoints={setOrderPoints} onCount={setOrdersInfo} />}
          <ZonesEditorMap
            zones={zones}
            selected={selected}
            onSelect={setSelected}
            onZoneCoords={setZoneCoords}
            onDrawArea={addZoneFromDraw}
            orders={showOrders ? orderPoints : []}
            height={460}
          />
        </>
      )}

      {!loading &&
        zones.map((z, zi) => (
          <div className={"mz-card" + (selected === zi ? " selected" : "")} key={zi}>
            <div className="mz-zone-head" onClick={() => setSelected(zi)}>
              <span className="mz-swatch" style={{ background: z.color }} />
              <input
                className="oc-input mz-name"
                value={z.name}
                onChange={(e) => setZone(zi, { name: e.target.value })}
                placeholder="Area name (e.g. North_Amman)"
              />
              <input
                className="oc-input mz-name-ar"
                dir="rtl"
                value={z.name_ar}
                onChange={(e) => setZone(zi, { name_ar: e.target.value })}
                placeholder="الاسم بالعربية"
              />
              <input
                className="oc-input mz-code"
                type="number"
                value={z.zone_code}
                onChange={(e) => setZone(zi, { zone_code: e.target.value })}
                placeholder="Code"
                title="Zone code (optional)"
              />
              <label className="mz-color">
                Color
                <input type="color" value={z.color} onChange={(e) => setZone(zi, { color: e.target.value })} />
              </label>
              <button className="mz-linkbtn danger" onClick={() => removeZone(zi)}>Remove area</button>
            </div>

            <table className="mz-table">
              <thead>
                <tr><th>#</th><th>Latitude</th><th>Longitude</th><th></th></tr>
              </thead>
              <tbody>
                {z.coordinates.map((c, pi) => (
                  <tr key={pi}>
                    <td>{pi + 1}</td>
                    <td>
                      <input className="oc-input" type="number" step="any" value={c.lat}
                        onChange={(e) => setPoint(zi, pi, { lat: e.target.value })} placeholder="31.99" />
                    </td>
                    <td>
                      <input className="oc-input" type="number" step="any" value={c.lng}
                        onChange={(e) => setPoint(zi, pi, { lng: e.target.value })} placeholder="35.90" />
                    </td>
                    <td>
                      <button className="mz-linkbtn danger" onClick={() => removePoint(zi, pi)} disabled={z.coordinates.length <= 3}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button className="mz-linkbtn" onClick={() => addPoint(zi)}>+ Add point</button>
          </div>
        ))}

      {!loading && (
        <div className="mz-actions">
          <button className="mz-addzone" onClick={addZone}>+ Add area</button>
          <div className="mz-spacer" />
          <input
            className="oc-input mz-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Version note (optional, e.g. 'added east split')"
          />
          <button className="oc-btn" onClick={load} disabled={saving}>Reset</button>
          <button className="oc-btn primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save zones"}</button>
        </div>
      )}
    </div>
  );
}
